import { TemporaryLocation, TemporaryLocationWithFriend } from '../../../types';

/**
 * Check if a temporary location is currently active
 */
export const isTempLocationCurrent = (tempLoc: TemporaryLocation | TemporaryLocationWithFriend): boolean => {
  const now = new Date();
  const start = new Date(tempLoc.start_datetime);
  const end = new Date(tempLoc.end_datetime);
  return now >= start && now <= end;
};

/**
 * Check if a temporary location is upcoming (not started yet)
 */
export const isTempLocationUpcoming = (tempLoc: TemporaryLocation | TemporaryLocationWithFriend): boolean => {
  const now = new Date();
  const start = new Date(tempLoc.start_datetime);
  return start > now;
};

/**
 * Helper function to determine if a place is a POI (named location) vs simple address
 * POIs: restaurants, museums, hospitals, parks, monuments, etc.
 * Addresses: streets, roads, residential addresses, etc.
 */
export const isPointOfInterest = (place: any): boolean => {
  const type = place.type?.toLowerCase() || '';
  const className = place.class?.toLowerCase() || '';

  // POI types (amenities, tourism, leisure)
  const poiTypes = [
    'restaurant', 'cafe', 'bar', 'pub', 'fast_food',
    'museum', 'gallery', 'monument', 'memorial', 'attraction',
    'hospital', 'clinic', 'pharmacy', 'doctors',
    'university', 'college', 'school',
    'park', 'garden', 'playground',
    'stadium', 'sports_centre', 'cinema', 'theatre',
    'hotel', 'hostel', 'guest_house',
    'shop', 'mall', 'supermarket',
    'station', 'railway_station', 'train_station', 'subway_entrance', 'tram_stop',
    'airport', 'aerodrome', 'helipad',
    'port', 'ferry_terminal',
    'church', 'mosque', 'synagogue', 'temple',
  ];

  const poiClasses = ['amenity', 'tourism', 'leisure', 'historic', 'shop', 'railway', 'aeroway'];

  // Check if name contains keywords that indicate it's a well-known place
  const nameKeywords = ['gare', 'station', 'aéroport', 'airport', 'musée', 'museum', 'hôpital', 'hospital'];
  const nameHasKeyword = place.name && nameKeywords.some(keyword =>
    place.name.toLowerCase().includes(keyword)
  );

  // Check if it's a POI type
  if (poiTypes.some(poiType => type.includes(poiType))) {
    return true;
  }

  // Check if it's a POI class
  if (poiClasses.includes(className)) {
    return true;
  }

  // Check if name contains POI keywords (gare, station, airport, etc.)
  if (nameHasKeyword) {
    return true;
  }

  // If it has a specific name (not just street address) and is not a simple street
  const hasSpecificName = place.name &&
                         !type.includes('street') &&
                         !type.includes('road') &&
                         !type.includes('house') &&
                         !type.includes('residential') &&
                         className !== 'highway';

  return hasSpecificName;
};

/**
 * Get full URL for a photo path
 */
export const getPhotoUrl = (photoPath?: string): string | null => {
  if (!photoPath) return null;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  // Use regex to only replace /api at the end of the URL
  return `${baseUrl.replace(/\/api$/, '')}${photoPath}`;
};
